import { useState, useEffect, Fragment, useRef, useMemo } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import {
  Activity,
  Clock,
  User,
  Search,
  Filter,
  Calendar,
  Shield,
  Smartphone,
  Globe,
  Database,
  ArrowRight,
  LogOut,
  LogIn,
  PlusCircle,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  RotateCcw,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  UserCheck,
  Stethoscope,
  Building2,
  CalendarDays,
  CreditCard,
  HelpCircle,
  Pill,
  Sparkles,
  Download,
  AlertTriangle,
  Flame,
  TrendingUp,
  Users,
  HardDrive
} from 'lucide-react';
import Pagination from '../../Components/common/Pagination';
import { TableSkeleton } from '../../Components/common/Skeleton';

// Format relative time helper
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 172800) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Categorize and style action
const classifyAction = (action = '') => {
  const act = action.toLowerCase();
  if (
    act.includes('delete') ||
    act.includes('cancel') ||
    act.includes('reject') ||
    act.includes('abort') ||
    act.includes('lost') ||
    act.includes('purge')
  ) {
    return {
      category: 'deleted',
      categoryLabel: 'DELETED',
      badgeBg: 'bg-rose-50 border-rose-200 text-rose-700',
      pillBg: 'bg-rose-500 text-white',
      dotColor: 'bg-rose-500',
      icon: Trash2
    };
  }

  if (
    act.includes('creat') ||
    act.includes('register') ||
    act.includes('add') ||
    act.includes('submit') ||
    act.includes('initiat') ||
    act.includes('upload') ||
    act.includes('booking')
  ) {
    return {
      category: 'added',
      categoryLabel: 'ADDED',
      badgeBg: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      pillBg: 'bg-emerald-600 text-white',
      dotColor: 'bg-emerald-500',
      icon: PlusCircle
    };
  }

  if (
    act.includes('verif') ||
    act.includes('agree') ||
    act.includes('approv')
  ) {
    return {
      category: 'verified',
      categoryLabel: 'VERIFIED',
      badgeBg: 'bg-purple-50 border-purple-200 text-purple-700',
      pillBg: 'bg-purple-600 text-white',
      dotColor: 'bg-purple-500',
      icon: CheckCircle2
    };
  }

  if (
    act.includes('login') ||
    act.includes('logout') ||
    act.includes('password') ||
    act.includes('otp')
  ) {
    return {
      category: 'auth',
      categoryLabel: 'AUTH',
      badgeBg: 'bg-amber-50 border-amber-200 text-amber-700',
      pillBg: 'bg-amber-500 text-white',
      dotColor: 'bg-amber-500',
      icon: Shield
    };
  }

  return {
    category: 'updated',
    categoryLabel: 'UPDATED',
    badgeBg: 'bg-sky-50 border-sky-200 text-sky-700',
    pillBg: 'bg-sky-600 text-white',
    dotColor: 'bg-sky-500',
    icon: Edit3
  };
};

const getEntityDetails = (type = '') => {
  switch (type.toLowerCase()) {
    case 'lead':
      return { label: 'Lead', icon: Activity, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' };
    case 'user':
      return { label: 'User', icon: User, color: 'text-blue-600 bg-blue-50 border-blue-200' };
    case 'doctor':
      return { label: 'Doctor', icon: Stethoscope, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
    case 'hospital':
      return { label: 'Hospital', icon: Building2, color: 'text-purple-600 bg-purple-50 border-purple-200' };
    case 'appointment':
      return { label: 'Appointment', icon: CalendarDays, color: 'text-teal-600 bg-teal-50 border-teal-200' };
    case 'surgery':
      return { label: 'Surgery', icon: Shield, color: 'text-violet-600 bg-violet-50 border-violet-200' };
    case 'prescription':
      return { label: 'Prescription', icon: Pill, color: 'text-pink-600 bg-pink-50 border-pink-200' };
    case 'payment':
    case 'payout':
      return { label: 'Payment / Payout', icon: CreditCard, color: 'text-amber-600 bg-amber-50 border-amber-200' };
    case 'ticket':
      return { label: 'Support Ticket', icon: HelpCircle, color: 'text-orange-600 bg-orange-50 border-orange-200' };
    default:
      return { label: type || 'System', icon: Database, color: 'text-slate-600 bg-slate-50 border-slate-200' };
  }
};

const formatActionName = (action = '') => {
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

const formatRoleName = (role = '') => {
  if (!role) return 'System';
  return role
    .replace(/_/g, ' ')
    .toUpperCase();
};

const SystemLogs = () => {
  const { user } = useAuth();
  const { socket } = useSocket() || {};

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity'); // 'activity' | 'sessions' | 'winston'
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  // Live Stats KPI
  const [stats, setStats] = useState({
    totalEvents: 0,
    todayEvents: 0,
    createdTotal: 0,
    updatedTotal: 0,
    deletedTotal: 0,
    topUsers: []
  });

  // Real-time security alerts banner
  const [securityAlerts, setSecurityAlerts] = useState([]);

  // Maintenance modal
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    actionCategory: 'all', // 'all' | 'added' | 'updated' | 'deleted' | 'verified' | 'auth'
    entityType: '',
    user: '',
    search: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 25
  });

  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1
  });

  // Winston Server logs state
  const [winstonFiles, setWinstonFiles] = useState([]);
  const [selectedWinstonFile, setSelectedWinstonFile] = useState('');
  const [winstonContent, setWinstonContent] = useState('');
  const [winstonLines, setWinstonLines] = useState(200);
  const [winstonLoading, setWinstonLoading] = useState(false);
  const [selectedWinstonDate, setSelectedWinstonDate] = useState('');
  const [selectedWinstonType, setSelectedWinstonType] = useState('requests');
  const [winstonSearch, setWinstonSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef(null);

  // Fetch users for filter dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/activity/users-list');
        if (res.data?.success && res.data?.data?.users) {
          setUsersList(res.data.data.users);
        }
      } catch (err) {
        console.error('Failed to load activity users:', err);
      }
    };
    fetchUsers();
    fetchStats();
  }, []);

  // Fetch statistics KPI
  const fetchStats = async () => {
    try {
      const res = await api.get('/activity/stats');
      if (res.data?.success && res.data?.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch activity stats:', err);
    }
  };

  // Socket Real-time Critical Alerts Listener
  useEffect(() => {
    if (!socket) return;
    const handleSecurityAlert = (data) => {
      setSecurityAlerts(prev => [data, ...prev.slice(0, 3)]);
      toast.error(`🚨 Critical Security Alert: ${data.description || data.action}`, { duration: 6000 });
      fetchLogs();
      fetchStats();
    };

    socket.on('critical_security_alert', handleSecurityAlert);
    return () => {
      socket.off('critical_security_alert', handleSecurityAlert);
    };
  }, [socket]);

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'activity' ? '/activity/logs' : '/activity/sessions';

      const queryParams = {
        page: filters.page,
        limit: filters.limit
      };

      if (activeTab === 'activity') {
        if (filters.actionCategory && filters.actionCategory !== 'all') {
          queryParams.actionCategory = filters.actionCategory;
        }
        if (filters.entityType) queryParams.entityType = filters.entityType;
        if (filters.user) queryParams.user = filters.user;
        if (filters.search) queryParams.search = filters.search.trim();
        if (filters.startDate) queryParams.startDate = filters.startDate;
        if (filters.endDate) queryParams.endDate = filters.endDate;
      } else {
        // Sessions tab
        if (filters.user) queryParams.userId = filters.user;
        if (filters.startDate) queryParams.startDate = filters.startDate;
        if (filters.endDate) queryParams.endDate = filters.endDate;
      }

      const params = new URLSearchParams(queryParams);
      const response = await api.get(`${endpoint}?${params}`);

      if (response.data?.success) {
        if (activeTab === 'activity') {
          setLogs(response.data.data.logs || []);
        } else {
          setLogs(response.data.data.sessions || []);
        }
        setPagination({
          total: response.data.data.total || 0,
          pages: response.data.data.pages || 1
        });
      }
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV Function
  const handleExportCSV = () => {
    if (!logs || logs.length === 0) {
      toast.error('No logs available to export');
      return;
    }

    const headers = [
      'Timestamp',
      'Performer Name',
      'Role',
      'Email',
      'Action Category',
      'Action Name',
      'Entity Type',
      'Entity ID',
      'Description',
      'IP Address',
      'Device/User Agent',
      'Status'
    ];

    const rows = logs.map(log => {
      const classification = classifyAction(log.action);
      return [
        `"${new Date(log.createdAt).toISOString()}"`,
        `"${log.user ? `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() : 'System Automated'}"`,
        `"${log.user?.role || 'system'}"`,
        `"${log.user?.email || ''}"`,
        `"${classification.categoryLabel}"`,
        `"${log.action || ''}"`,
        `"${log.entityType || ''}"`,
        `"${log.entityId || ''}"`,
        `"${(log.description || '').replace(/"/g, '""')}"`,
        `"${log.ipAddress || ''}"`,
        `"${(log.userAgent || '').replace(/"/g, '""')}"`,
        `"${log.status || 'success'}"`
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Doxez_Audit_Logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${logs.length} audit records to CSV`);
  };

  // Safe Log Cleanup Handler
  const handleCleanupSubmit = async () => {
    try {
      setCleanupLoading(true);
      const res = await api.post(`/activity/cleanup?days=${cleanupDays}`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Log cleanup completed');
        setShowCleanupModal(false);
        fetchLogs();
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cleanup old logs');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Winston helpers
  const fetchWinstonFiles = async () => {
    try {
      setWinstonLoading(true);
      const res = await api.get('/activity/winston/files');
      if (res.data?.success) {
        setWinstonFiles(res.data.data.files);
        if (res.data.data.files.length > 0 && !selectedWinstonFile) {
          setSelectedWinstonFile(res.data.data.files[0].name);
        }
      }
    } catch (err) {
      toast.error('Failed to load system log files list');
    } finally {
      setWinstonLoading(false);
    }
  };

  const fetchWinstonContent = async (filename, linesCount) => {
    if (!filename) return;
    try {
      setWinstonLoading(true);
      const res = await api.get(`/activity/winston/content?filename=${filename}&lines=${linesCount || winstonLines}`);
      if (res.data?.success) {
        setWinstonContent(res.data.data.content);
      }
    } catch (err) {
      toast.error('Failed to load log file content');
    } finally {
      setWinstonLoading(false);
    }
  };

  const parsedLogsData = winstonFiles.reduce((acc, file) => {
    const match = file.name.match(/^(combined|error|requests|http)-(\d{4}-\d{2}-\d{2})\.log$/);
    if (match) {
      const [_, type, date] = match;
      if (!acc[date]) {
        acc[date] = {
          date,
          types: {},
          displayLabel: new Date(date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
        };
      }
      acc[date].types[type] = file;
    }
    return acc;
  }, {});

  const availableDates = Object.values(parsedLogsData).sort((a, b) => new Date(b.date) - new Date(a.date));

  // Trigger logs fetch on tab / filter change
  useEffect(() => {
    if (activeTab === 'winston') {
      fetchWinstonFiles();
    } else {
      fetchLogs();
    }
  }, [
    activeTab,
    filters.page,
    filters.limit,
    filters.actionCategory,
    filters.entityType,
    filters.user,
    filters.startDate,
    filters.endDate
  ]);

  // Debounced search trigger
  useEffect(() => {
    if (activeTab === 'winston') return;
    const timer = setTimeout(() => {
      fetchLogs();
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Winston synchronization effects
  useEffect(() => {
    if (activeTab === 'winston' && winstonFiles.length > 0) {
      const dates = Object.keys(parsedLogsData).sort((a, b) => new Date(b) - new Date(a));
      if (dates.length > 0 && !selectedWinstonDate) {
        setSelectedWinstonDate(dates[0]);
      }
    }
  }, [activeTab, winstonFiles]);

  useEffect(() => {
    if (activeTab === 'winston' && selectedWinstonDate && selectedWinstonType) {
      const dateData = parsedLogsData[selectedWinstonDate];
      const fileObj = dateData?.types[selectedWinstonType];
      if (fileObj) {
        setSelectedWinstonFile(fileObj.name);
      } else {
        setSelectedWinstonFile('');
        setWinstonContent('No logs of this type recorded for the selected date.');
      }
    }
  }, [activeTab, selectedWinstonDate, selectedWinstonType, winstonFiles]);

  useEffect(() => {
    if (activeTab === 'winston' && selectedWinstonFile) {
      fetchWinstonContent(selectedWinstonFile, winstonLines);
    }
  }, [activeTab, selectedWinstonFile, winstonLines]);

  useEffect(() => {
    if (activeTab === 'winston' && autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [activeTab, winstonContent, winstonSearch, autoScroll]);

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success('ID copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUserClick = (userId) => {
    setFilters(prev => ({
      ...prev,
      user: userId,
      page: 1
    }));
  };

  const handleClearUserFilter = () => {
    setFilters(prev => ({ ...prev, user: '', page: 1 }));
  };

  const handleCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, actionCategory: category, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      actionCategory: 'all',
      entityType: '',
      user: '',
      search: '',
      startDate: '',
      endDate: '',
      page: 1,
      limit: 25
    });
  };

  // Date preset helper
  const applyDatePreset = (preset) => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];

    if (preset === 'today') {
      const str = formatDate(today);
      setFilters(prev => ({ ...prev, startDate: str, endDate: str, page: 1 }));
    } else if (preset === 'yesterday') {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const str = formatDate(y);
      setFilters(prev => ({ ...prev, startDate: str, endDate: str, page: 1 }));
    } else if (preset === 'week') {
      const past = new Date();
      past.setDate(past.getDate() - 7);
      setFilters(prev => ({ ...prev, startDate: formatDate(past), endDate: formatDate(today), page: 1 }));
    } else if (preset === 'month') {
      const past = new Date();
      past.setDate(past.getDate() - 30);
      setFilters(prev => ({ ...prev, startDate: formatDate(past), endDate: formatDate(today), page: 1 }));
    } else if (preset === 'clear') {
      setFilters(prev => ({ ...prev, startDate: '', endDate: '', page: 1 }));
    }
  };

  const activeUserObject = useMemo(() => {
    if (!filters.user) return null;
    return usersList.find(u => u._id === filters.user);
  }, [filters.user, usersList]);

  // Render log lines for Winston
  const renderLogLines = () => {
    if (!winstonContent) return <div className="text-slate-500 italic p-2">No log data found in this file.</div>;

    let lines = winstonContent.split('\n');
    if (winstonSearch) {
      lines = lines.filter(line => line.toLowerCase().includes(winstonSearch.toLowerCase()));
    }
    if (lines.length === 0) return <div className="text-slate-500 italic p-2">No lines match your search filter.</div>;

    return lines.map((line, idx) => {
      if (!line.trim()) return null;
      let colorClass = 'text-slate-300';
      const lineLower = line.toLowerCase();

      if (lineLower.includes('[error]') || lineLower.includes('error:') || lineLower.includes(' 500 ') || lineLower.includes(' 500 -')) {
        colorClass = 'text-rose-400 font-bold';
      } else if (lineLower.includes('[warn]') || lineLower.includes(' 400 ') || lineLower.includes(' 401 ') || lineLower.includes(' 403 ')) {
        colorClass = 'text-amber-400';
      } else if (lineLower.includes('[info]') || lineLower.includes(' 200 ') || lineLower.includes(' 201 ')) {
        colorClass = 'text-emerald-400';
      } else if (lineLower.includes('[debug]') || lineLower.includes('[http]')) {
        colorClass = 'text-cyan-400';
      }

      return (
        <div key={idx} className={`${colorClass} hover:bg-slate-800/60 px-2 py-0.5 rounded transition-all font-mono whitespace-pre-wrap`}>
          {line}
        </div>
      );
    });
  };

  const hasActiveFilters = Boolean(
    filters.actionCategory !== 'all' ||
    filters.entityType ||
    filters.user ||
    filters.search ||
    filters.startDate ||
    filters.endDate
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1700px] mx-auto">
      {/* 🧭 Top Navigation & Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#143E78] text-white flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Audit & System Activity Logs</h1>
              <p className="text-slate-500 text-xs font-semibold mt-0.5">
                Track who added, updated, or deleted records — complete timeline and filters
              </p>
            </div>
          </div>
        </div>

        {/* Tab switchers & Tools */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
            <button
              onClick={() => { setActiveTab('activity'); setFilters(f => ({ ...f, page: 1 })); }}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'activity'
                  ? 'bg-white text-[#143E78] shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Activity className="w-4 h-4" />
              <span>Audit Logs</span>
            </button>
            <button
              onClick={() => { setActiveTab('sessions'); setFilters(f => ({ ...f, page: 1 })); }}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'sessions'
                  ? 'bg-white text-[#143E78] shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Session History</span>
            </button>
            <button
              onClick={() => { setActiveTab('winston'); }}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'winston'
                  ? 'bg-white text-[#143E78] shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Database className="w-4 h-4" />
              <span>Server Log Files</span>
            </button>
          </div>

          {/* Action Buttons: Export CSV & Maintenance */}
          {activeTab !== 'winston' && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                className="px-4 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider shadow-md shadow-emerald-700/20 transition-all flex items-center gap-2"
                title="Export currently filtered audit logs to CSV"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>

              {user?.role === 'admin' && (
                <button
                  onClick={() => setShowCleanupModal(true)}
                  className="px-3.5 py-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5"
                  title="Log Maintenance & Archival"
                >
                  <HardDrive className="w-4 h-4 text-slate-600" />
                  <span>Maintenance</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 🚨 REAL-TIME SECURITY ALERTS BANNER (If any critical events triggered) */}
      {securityAlerts.length > 0 && (
        <div className="bg-rose-50 border border-rose-300 rounded-3xl p-4 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-rose-800 uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 animate-pulse" />
              Real-time Critical Security Alerts ({securityAlerts.length})
            </span>
            <button
              onClick={() => setSecurityAlerts([])}
              className="text-xs font-bold text-rose-600 hover:underline"
            >
              Dismiss
            </button>
          </div>
          <div className="divide-y divide-rose-200/60 text-xs font-bold text-rose-900">
            {securityAlerts.map((alert, idx) => (
              <div key={idx} className="py-1.5 flex items-center justify-between">
                <span>{alert.description || alert.action}</span>
                <span className="text-[10px] text-rose-500 font-mono">{formatRelativeTime(alert.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 📊 LIVE KPI STATS BAR */}
      {activeTab === 'activity' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Total Events</span>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-xl font-black text-slate-900">{stats.totalEvents || pagination.total}</span>
                {stats.todayEvents > 0 && (
                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                    +{stats.todayEvents} today
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <PlusCircle className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Created (Added)</span>
              <span className="text-xl font-black text-slate-900 mt-0.5 block">{stats.createdTotal || 0}</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
              <Edit3 className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Modifications</span>
              <span className="text-xl font-black text-slate-900 mt-0.5 block">{stats.updatedTotal || 0}</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Deletions (Risk)</span>
              <span className="text-xl font-black text-rose-600 mt-0.5 block">{stats.deletedTotal || 0}</span>
            </div>
          </div>

          <div className="col-span-2 sm:col-span-1 bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
              <Flame className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Top Performer</span>
              <span className="text-xs font-black text-slate-800 truncate block mt-0.5">
                {stats.topUsers?.[0]?.name || 'N/A'}
              </span>
              <span className="text-[10px] font-bold text-indigo-600 block">
                {stats.topUsers?.[0]?.count ? `${stats.topUsers[0].count} actions` : 'No logs'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 ACTIVITY LOGS / SESSIONS CONTROLS */}
      {activeTab !== 'winston' ? (
        <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-xl shadow-slate-200/50 border border-slate-200 space-y-5">
          {/* Action Category Pills (Only in Activity tab) */}
          {activeTab === 'activity' && (
            <div className="flex flex-col gap-2 pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5" /> Action Category Filter
                </span>
                {filters.actionCategory !== 'all' && (
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className="text-[10px] font-bold text-indigo-600 hover:underline"
                  >
                    Reset Action
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleCategoryChange('all')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 ${
                    filters.actionCategory === 'all'
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  All Actions
                </button>

                <button
                  onClick={() => handleCategoryChange('added')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border ${
                    filters.actionCategory === 'added'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-md ring-2 ring-emerald-500/20'
                      : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  🟢 Added / Created
                </button>

                <button
                  onClick={() => handleCategoryChange('updated')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border ${
                    filters.actionCategory === 'updated'
                      ? 'bg-sky-600 text-white border-sky-600 shadow-md ring-2 ring-sky-500/20'
                      : 'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100'
                  }`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  🔵 Updated / Modified
                </button>

                <button
                  onClick={() => handleCategoryChange('deleted')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border ${
                    filters.actionCategory === 'deleted'
                      ? 'bg-rose-600 text-white border-rose-600 shadow-md ring-2 ring-rose-500/20'
                      : 'bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100'
                  }`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  🔴 Deleted / Cancelled
                </button>

                <button
                  onClick={() => handleCategoryChange('verified')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border ${
                    filters.actionCategory === 'verified'
                      ? 'bg-purple-600 text-white border-purple-600 shadow-md ring-2 ring-purple-500/20'
                      : 'bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  🟣 Verified / Approved
                </button>

                <button
                  onClick={() => handleCategoryChange('auth')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border ${
                    filters.actionCategory === 'auth'
                      ? 'bg-amber-600 text-white border-amber-600 shadow-md ring-2 ring-amber-500/20'
                      : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                  }`}
                >
                  <Shield className="w-3.5 h-3.5" />
                  🟡 Logins / Security
                </button>
              </div>
            </div>
          )}

          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
            {/* 1. Keyword Search */}
            <div className="lg:col-span-3">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Search Keyword
              </label>
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  placeholder="Patient name, description, user, ID..."
                  className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] focus:bg-white transition-all shadow-sm"
                />
                {filters.search && (
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, search: '', page: 1 }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* 2. User Dropdown */}
            <div className="lg:col-span-3">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1 flex items-center justify-between">
                <span>Who Performed</span>
                {filters.user && (
                  <button onClick={handleClearUserFilter} className="text-[10px] text-rose-600 font-bold hover:underline">
                    Clear
                  </button>
                )}
              </label>
              <div className="relative">
                <select
                  value={filters.user}
                  onChange={(e) => setFilters(prev => ({ ...prev, user: e.target.value, page: 1 }))}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] focus:bg-white transition-all shadow-sm appearance-none pr-8 truncate"
                >
                  <option value="">All Users & Staff</option>
                  {usersList.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.firstName} {u.lastName} ({formatRoleName(u.role)})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* 3. Entity Type Dropdown */}
            {activeTab === 'activity' ? (
              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                  Target Entity
                </label>
                <div className="relative">
                  <select
                    value={filters.entityType}
                    onChange={(e) => setFilters(prev => ({ ...prev, entityType: e.target.value, page: 1 }))}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] focus:bg-white transition-all shadow-sm appearance-none pr-8"
                  >
                    <option value="">All Entities</option>
                    <option value="lead">Leads</option>
                    <option value="user">Users & Staff</option>
                    <option value="doctor">Doctors</option>
                    <option value="hospital">Hospitals</option>
                    <option value="appointment">Appointments</option>
                    <option value="surgery">Surgeries</option>
                    <option value="prescription">Prescriptions</option>
                    <option value="payment">Payments</option>
                    <option value="payout">Payouts</option>
                    <option value="ticket">Support Tickets</option>
                    <option value="doctor_availability">Doctor Availability</option>
                    <option value="system">System</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
            ) : (
              <div className="lg:col-span-2" />
            )}

            {/* 4. Date Range: Start Date & End Date */}
            <div className="lg:col-span-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] shadow-sm"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] shadow-sm"
              />
            </div>
          </div>

          {/* Quick Date Presets & Reset Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider mr-1">Quick Dates:</span>
              <button
                onClick={() => applyDatePreset('today')}
                className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold transition-all"
              >
                Today
              </button>
              <button
                onClick={() => applyDatePreset('yesterday')}
                className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold transition-all"
              >
                Yesterday
              </button>
              <button
                onClick={() => applyDatePreset('week')}
                className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold transition-all"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => applyDatePreset('month')}
                className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold transition-all"
              >
                Last 30 Days
              </button>
              {(filters.startDate || filters.endDate) && (
                <button
                  onClick={() => applyDatePreset('clear')}
                  className="px-3 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-bold transition-all"
                >
                  Clear Date
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-1.5 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset All Filters
                </button>
              )}

              <button
                onClick={() => { fetchLogs(); fetchStats(); }}
                className="px-5 py-2 rounded-xl bg-[#143E78] hover:bg-[#0d2a52] text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-blue-900/20 transition-all active:scale-95 flex items-center gap-2"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh Logs
              </button>
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Filters:</span>

              {filters.actionCategory !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-full text-xs font-bold">
                  Action: {filters.actionCategory.toUpperCase()}
                  <button onClick={() => handleCategoryChange('all')} className="text-indigo-600 hover:text-indigo-900 ml-1">✕</button>
                </span>
              )}

              {activeUserObject && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full text-xs font-bold">
                  Performer: {activeUserObject.firstName} {activeUserObject.lastName}
                  <button onClick={handleClearUserFilter} className="text-blue-600 hover:text-blue-900 ml-1">✕</button>
                </span>
              )}

              {filters.entityType && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 border border-teal-200 text-teal-800 rounded-full text-xs font-bold">
                  Entity: {filters.entityType.toUpperCase()}
                  <button onClick={() => setFilters(prev => ({ ...prev, entityType: '', page: 1 }))} className="text-teal-600 hover:text-teal-900 ml-1">✕</button>
                </span>
              )}

              {filters.search && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 border border-slate-200 text-slate-800 rounded-full text-xs font-bold">
                  Search: "{filters.search}"
                  <button onClick={() => setFilters(prev => ({ ...prev, search: '', page: 1 }))} className="text-slate-500 hover:text-slate-800 ml-1">✕</button>
                </span>
              )}

              {(filters.startDate || filters.endDate) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full text-xs font-bold">
                  Date: {filters.startDate || 'Any'} → {filters.endDate || 'Now'}
                  <button onClick={() => applyDatePreset('clear')} className="text-amber-600 hover:text-amber-900 ml-1">✕</button>
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        /* WINSTON LOG CONTROLS */
        <div className="bg-white p-5 sm:p-6 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:flex gap-4 sm:gap-6 items-end">
          <div className="flex-1 min-w-0 sm:min-w-[180px]">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1">Select Date</label>
            <select
              value={selectedWinstonDate}
              onChange={(e) => setSelectedWinstonDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] transition-all shadow-sm"
            >
              {availableDates.map(d => (
                <option key={d.date} value={d.date}>
                  {d.displayLabel}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-0 sm:min-w-[180px]">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1">Log Type / Level</label>
            <select
              value={selectedWinstonType}
              onChange={(e) => setSelectedWinstonType(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] transition-all shadow-sm"
            >
              <option value="requests">Requests (Traffic logs)</option>
              <option value="combined">Combined (System Info)</option>
              <option value="error">Error (Only Failures/Errors)</option>
              <option value="http">HTTP (Express Requests)</option>
            </select>
          </div>

          <div className="flex-1 min-w-0 sm:min-w-[150px]">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1">Lines to View</label>
            <select
              value={winstonLines}
              onChange={(e) => setWinstonLines(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78] transition-all shadow-sm"
            >
              <option value="50">Last 50 lines</option>
              <option value="100">Last 100 lines</option>
              <option value="200">Last 200 lines</option>
              <option value="500">Last 500 lines</option>
              <option value="1000">Last 1000 lines</option>
            </select>
          </div>

          <div>
            <button
              onClick={() => fetchWinstonContent(selectedWinstonFile, winstonLines)}
              className="w-full sm:w-auto px-8 py-3 bg-[#143E78] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#0d2a52] transition-all shadow-lg shadow-blue-900/20 active:scale-95 flex items-center gap-2 justify-center"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh File
            </button>
          </div>
        </div>
      )}

      {/* 📊 DATA DISPLAY TABLE / CARDS */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
        {/* Table / Winston View */}
        {activeTab === 'winston' ? (
          winstonLoading ? (
            <div className="p-6">
              <TableSkeleton rows={10} />
            </div>
          ) : (
            <div className="p-6 space-y-4">
              <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex flex-col">
                  <span className="text-xs font-black text-slate-700 uppercase">Active File: {selectedWinstonFile || 'None'}</span>
                  <span className="text-[10px] font-bold text-slate-400 mt-0.5">Showing last {winstonLines} lines</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                  <div className="relative flex-1 md:flex-initial">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={winstonSearch}
                      onChange={(e) => setWinstonSearch(e.target.value)}
                      placeholder="Filter lines..."
                      className="w-full md:w-[220px] pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-[#143E78]"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-xs font-bold text-slate-600 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                      className="rounded text-[#143E78] focus:ring-[#143E78]"
                    />
                    <span>Auto-scroll</span>
                  </label>
                </div>
              </div>

              <div
                ref={logContainerRef}
                className="p-6 bg-slate-900 rounded-3xl overflow-auto max-h-[600px] text-left leading-relaxed shadow-inner"
              >
                {renderLogLines()}
              </div>
            </div>
          )
        ) : loading ? (
          <div className="p-6">
            <TableSkeleton rows={10} />
          </div>
        ) : (
          <>
            {/* 📱 Mobile Card View */}
            <div className="block lg:hidden divide-y divide-slate-100">
              {logs.length > 0 ? (
                logs.map((log) => {
                  const classification = classifyAction(log.action);
                  const entity = getEntityDetails(log.entityType);
                  const Icon = classification.icon;
                  const EntityIcon = entity.icon;
                  const isExpanded = expandedLogId === log._id;

                  return (
                    <div key={log._id} className="p-5 space-y-3.5 hover:bg-slate-50 transition-colors">
                      {/* Top Bar: Action badge & Timestamp */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border flex items-center gap-1.5 ${classification.badgeBg}`}>
                            <Icon className="w-3 h-3" />
                            {classification.categoryLabel}
                          </span>
                          <span className="text-[11px] font-black text-slate-800">
                            {formatActionName(log.action)}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] font-bold text-slate-400 block">
                            {formatRelativeTime(log.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* User Performer Card */}
                      <div
                        onClick={() => log.user?._id && handleUserClick(log.user._id)}
                        className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                          log.user?._id
                            ? 'bg-slate-50 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer'
                            : 'bg-slate-50/50 border-slate-100 text-slate-400'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-black text-xs shrink-0">
                          {log.user ? (
                            `${(log.user.firstName || '')[0] || ''}${(log.user.lastName || '')[0] || ''}`.toUpperCase() || 'U'
                          ) : (
                            <Shield className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-800 truncate">
                              {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System Automated'}
                            </span>
                            <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 uppercase">
                              {formatRoleName(log.user?.role)}
                            </span>
                          </div>
                          <span className="text-[10px] font-semibold text-slate-400 truncate block">
                            {log.user?.email || 'System Action'}
                          </span>
                        </div>
                      </div>

                      {/* Description & Target */}
                      <div className="bg-slate-50/60 p-3.5 rounded-2xl border border-slate-100 space-y-2">
                        <p className="text-xs font-bold text-slate-700 leading-relaxed">
                          {log.description}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-200/60 text-[11px]">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border text-[10px] font-black uppercase ${entity.color}`}>
                            <EntityIcon className="w-3 h-3" />
                            {entity.label}
                          </span>
                          {log.entityId && (
                            <button
                              onClick={() => handleCopyId(log.entityId)}
                              className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-slate-800 bg-white px-2 py-0.5 rounded-md border border-slate-200"
                            >
                              <span>ID: {String(log.entityId).slice(0, 10)}...</span>
                              {copiedId === log.entityId ? <Check className="w-2.5 h-2.5 text-emerald-600" /> : <Copy className="w-2.5 h-2.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Footer: Device, IP, Details Toggle */}
                      <div className="flex items-center justify-between text-[10px] font-semibold text-slate-400 pt-1">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5" />
                          <span className="font-mono">{log.ipAddress || '127.0.0.1'}</span>
                        </div>
                        <button
                          onClick={() => setExpandedLogId(isExpanded ? null : log._id)}
                          className="text-[#143E78] font-black uppercase tracking-wider hover:underline flex items-center gap-1"
                        >
                          {isExpanded ? 'Hide Details ▲' : 'View Details ▼'}
                        </button>
                      </div>

                      {/* Expanded Change Details (Diff & Context) */}
                      {isExpanded && (
                        <div className="bg-slate-900 text-slate-200 p-4 rounded-2xl space-y-3 text-left">
                          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Log Context Details</span>
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${log.status === 'failed' ? 'bg-rose-900/60 text-rose-300' : 'bg-emerald-900/60 text-emerald-300'}`}>
                              {log.status || 'Success'}
                            </span>
                          </div>

                          {(log.previousValue || log.newValue) && (
                            <div className="space-y-2">
                              {log.previousValue && (
                                <div>
                                  <span className="text-[10px] font-black text-rose-400 uppercase tracking-wider block">Before (Previous Value)</span>
                                  <pre className="mt-1 p-2.5 bg-rose-950/40 border border-rose-900/40 text-rose-200 text-[10px] font-mono rounded-xl overflow-x-auto">
                                    {typeof log.previousValue === 'object' ? JSON.stringify(log.previousValue, null, 2) : String(log.previousValue)}
                                  </pre>
                                </div>
                              )}
                              {log.newValue && (
                                <div>
                                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider block">After (New Value)</span>
                                  <pre className="mt-1 p-2.5 bg-emerald-950/40 border border-emerald-900/40 text-emerald-200 text-[10px] font-mono rounded-xl overflow-x-auto">
                                    {typeof log.newValue === 'object' ? JSON.stringify(log.newValue, null, 2) : String(log.newValue)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}

                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <div>
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Metadata</span>
                              <pre className="mt-1 p-2.5 bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-mono rounded-xl overflow-x-auto">
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-16 text-center flex flex-col items-center gap-3 opacity-40">
                  <Activity className="w-12 h-12" />
                  <p className="text-sm font-black uppercase tracking-widest">No Activity Records Found</p>
                  <p className="text-xs text-slate-500">Try adjusting your filters or date range</p>
                </div>
              )}
            </div>

            {/* 💻 Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#143E78] text-white">
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90">Timestamp</th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90">Performer</th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90">Action</th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90">Target Entity</th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90">Description & Context</th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase tracking-widest opacity-90 text-right">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.length > 0 ? (
                    logs.map((log) => {
                      const classification = classifyAction(log.action);
                      const entity = getEntityDetails(log.entityType);
                      const Icon = classification.icon;
                      const EntityIcon = entity.icon;
                      const isExpanded = expandedLogId === log._id;

                      return (
                        <Fragment key={log._id}>
                          <tr className={`hover:bg-slate-50/80 transition-colors ${isExpanded ? 'bg-slate-50/90' : ''}`}>
                            {/* 1. Timestamp */}
                            <td className="px-6 py-4 align-top whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-xs font-black text-slate-900">
                                  {new Date(log.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                                <span className="text-[11px] font-bold text-slate-500 mt-0.5">
                                  {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                                <span className="text-[10px] font-extrabold text-indigo-600 mt-1">
                                  {formatRelativeTime(log.createdAt)}
                                </span>
                              </div>
                            </td>

                            {/* 2. Performer */}
                            <td className="px-6 py-4 align-top">
                              {log.user ? (
                                <button
                                  onClick={() => handleUserClick(log.user._id)}
                                  className="flex items-center gap-3 text-left group hover:opacity-90 transition-all"
                                  title="Click to filter by this user"
                                >
                                  <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 group-hover:border-indigo-400 text-slate-700 flex items-center justify-center font-black text-xs shrink-0">
                                    {`${(log.user.firstName || '')[0] || ''}${(log.user.lastName || '')[0] || ''}`.toUpperCase() || 'U'}
                                  </div>
                                  <div className="flex flex-col min-w-0 max-w-[180px]">
                                    <span className="text-xs font-black text-slate-800 truncate group-hover:text-indigo-600">
                                      {log.user.firstName} {log.user.lastName}
                                    </span>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <span className="text-[9px] font-black px-2 py-0.2 rounded-md bg-indigo-50 text-indigo-700 uppercase tracking-tight">
                                        {formatRoleName(log.user.role)}
                                      </span>
                                    </div>
                                    <span className="text-[10px] font-medium text-slate-400 truncate mt-0.5">
                                      {log.user.email}
                                    </span>
                                  </div>
                                </button>
                              ) : (
                                <div className="flex items-center gap-2 text-slate-400">
                                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                    <Shield className="w-4 h-4" />
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-black text-slate-600">System Automated</span>
                                    <span className="text-[10px] font-bold text-slate-400">Background Process</span>
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* 3. Action Category & Name */}
                            <td className="px-6 py-4 align-top">
                              <div className="flex flex-col gap-1.5 items-start">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${classification.badgeBg}`}>
                                  <Icon className="w-3.5 h-3.5" />
                                  {classification.categoryLabel}
                                </span>
                                <span className="text-xs font-black text-slate-800">
                                  {formatActionName(log.action)}
                                </span>
                              </div>
                            </td>

                            {/* 4. Target Entity */}
                            <td className="px-6 py-4 align-top">
                              <div className="flex flex-col gap-1 items-start">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-wider ${entity.color}`}>
                                  <EntityIcon className="w-3.5 h-3.5" />
                                  {entity.label}
                                </span>
                                {log.entityId && (
                                  <button
                                    onClick={() => handleCopyId(log.entityId)}
                                    className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded transition-all mt-0.5"
                                    title="Click to copy full ID"
                                  >
                                    <span>{String(log.entityId).slice(0, 12)}...</span>
                                    {copiedId === log.entityId ? <Check className="w-2.5 h-2.5 text-emerald-600" /> : <Copy className="w-2.5 h-2.5" />}
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* 5. Description & Device/IP */}
                            <td className="px-6 py-4 align-top max-w-[340px]">
                              <div className="space-y-1.5">
                                <p className="text-xs font-bold text-slate-700 leading-relaxed">
                                  {log.description}
                                </p>
                                <div className="flex items-center gap-3 text-[10px] font-semibold text-slate-400">
                                  <span className="flex items-center gap-1 font-mono">
                                    <Globe className="w-3 h-3" /> {log.ipAddress || '127.0.0.1'}
                                  </span>
                                  {log.userAgent && (
                                    <span className="flex items-center gap-1 truncate max-w-[140px]" title={log.userAgent}>
                                      <Smartphone className="w-3 h-3 shrink-0" /> {log.userAgent}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* 6. Details Button */}
                            <td className="px-6 py-4 align-top text-right whitespace-nowrap">
                              <button
                                onClick={() => setExpandedLogId(isExpanded ? null : log._id)}
                                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border transition-all flex items-center gap-1.5 ml-auto ${
                                  isExpanded
                                    ? 'bg-[#143E78] text-white border-[#143E78]'
                                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                                }`}
                              >
                                <span>{isExpanded ? 'Hide' : 'Details'}</span>
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Diff & Context Row */}
                          {isExpanded && (
                            <tr className="bg-slate-900 text-slate-100">
                              <td colSpan="6" className="px-8 py-5 border-t border-slate-800">
                                <div className="space-y-4 text-left">
                                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-3">
                                    <div className="flex items-center gap-3">
                                      <span className="text-xs font-black text-slate-300 uppercase tracking-wider">
                                        Audit Details: {formatActionName(log.action)}
                                      </span>
                                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${log.status === 'failed' ? 'bg-rose-900/60 text-rose-300' : 'bg-emerald-900/60 text-emerald-300'}`}>
                                        Status: {log.status || 'Success'}
                                      </span>
                                    </div>
                                    <div className="text-[11px] font-mono text-slate-400">
                                      Log Record ID: {log._id}
                                    </div>
                                  </div>

                                  {/* Side-by-side Before vs After diff */}
                                  {(log.previousValue || log.newValue) ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {log.previousValue && (
                                        <div>
                                          <span className="text-[11px] font-black text-rose-400 uppercase tracking-widest block mb-1.5">
                                            🔻 Previous Value (Before Change)
                                          </span>
                                          <pre className="p-3 bg-rose-950/40 border border-rose-900/50 text-rose-200 text-[11px] font-mono rounded-2xl overflow-x-auto max-h-[300px]">
                                            {typeof log.previousValue === 'object' ? JSON.stringify(log.previousValue, null, 2) : String(log.previousValue)}
                                          </pre>
                                        </div>
                                      )}
                                      {log.newValue && (
                                        <div>
                                          <span className="text-[11px] font-black text-emerald-400 uppercase tracking-widest block mb-1.5">
                                            🔺 New Value (After Change)
                                          </span>
                                          <pre className="p-3 bg-emerald-950/40 border border-emerald-900/50 text-emerald-200 text-[11px] font-mono rounded-2xl overflow-x-auto max-h-[300px]">
                                            {typeof log.newValue === 'object' ? JSON.stringify(log.newValue, null, 2) : String(log.newValue)}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}

                                  {/* Metadata */}
                                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                                    <div>
                                      <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                                        Payload & Activity Metadata
                                      </span>
                                      <pre className="p-3 bg-slate-800/80 border border-slate-700 text-slate-200 text-[11px] font-mono rounded-2xl overflow-x-auto max-h-[250px]">
                                        {JSON.stringify(log.metadata, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-8 py-20 text-center">
                        <div className="flex flex-col items-center gap-3 opacity-30">
                          <Activity className="w-16 h-16" />
                          <p className="text-xl font-black uppercase tracking-widest">No Activity Records Found</p>
                          <p className="text-xs text-slate-500 font-bold">Try adjusting your filters or date range</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 📟 PAGINATION BAR */}
      {activeTab !== 'winston' && (
        <div className="px-6 py-4 bg-white rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-4 text-xs font-bold text-slate-500">
            <span>
              Showing {pagination.total > 0 ? (filters.page - 1) * filters.limit + 1 : 0} to{' '}
              {Math.min(filters.page * filters.limit, pagination.total)} of {pagination.total} entries
            </span>

            <div className="flex items-center gap-1.5 pl-4 border-l border-slate-200">
              <span className="text-[10px] uppercase font-black text-slate-400">Rows per page:</span>
              <select
                value={filters.limit}
                onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-[#143E78]"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={filters.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setFilters(f => ({ ...f, page }))}
            />
          )}
        </div>
      )}

      {/* 🛡️ LOG MAINTENANCE MODAL (Admin Only) */}
      {showCleanupModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <HardDrive className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Log Maintenance & Archival</h3>
                <p className="text-xs text-slate-500 font-semibold">Safe ephemeral cleanup to reduce database storage</p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-600 space-y-2">
              <p className="font-bold text-slate-800">Security Guarantee:</p>
              <p>
                Only ephemeral session logs (<code className="text-indigo-600">user_login</code>, <code className="text-indigo-600">user_logout</code>, <code className="text-indigo-600">otp</code>) older than your selected days will be purged.
              </p>
              <p className="text-emerald-700 font-bold">
                ✓ All business audit logs (Leads, Patients, Hospitals, Surgeries, Payments, MoU) are permanently preserved.
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">
                Purge sessions older than:
              </label>
              <select
                value={cleanupDays}
                onChange={(e) => setCleanupDays(parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-[#143E78]"
              >
                <option value="30">Older than 30 Days (1 Month)</option>
                <option value="60">Older than 60 Days (2 Months)</option>
                <option value="90">Older than 90 Days (Recommended - 3 Months)</option>
                <option value="180">Older than 180 Days (6 Months)</option>
                <option value="365">Older than 365 Days (1 Year)</option>
              </select>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowCleanupModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-black uppercase tracking-wider transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanupSubmit}
                disabled={cleanupLoading}
                className="px-6 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-rose-700/20 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {cleanupLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                Run Maintenance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemLogs;
